import PropTypes from "prop-types";
import React from "react";
import { Box, Flex, Image, Text, Title, useMantineTheme } from "@mantine/core";
import { useStylesLanding } from "../../utils/mantine/useStylesLanding";
import GuaranteedDataBlock from "./GuaranteedDataBlock";

const GuaranteedData = ({ staticBasePath }) => {
  // Mantine theme instance to apply custom styles and theming throughout the component
  const theme = useMantineTheme();

  const { classes: landingClasses } = useStylesLanding();
  return (
    <Box component="section" className={landingClasses.guaranteed}>
      <Box className={landingClasses.landingContainer}>
        <Box className={landingClasses.guaranteedTitle}>
          Data Validation as a Service
        </Box>

        <Text className={landingClasses.guaranteedText}>
          vBase offers a solution for businesses that need consistent and
          trustworthy data and documents. Assure your data’s timeliness and
          integrity are verifiable, and prove the authorship of any document
          with vBase’s independently verifiable, tamperproof data stamps.
        </Text>

        <Box className={landingClasses.guaranteedBlocks}>
          <Flex
            align={theme.flex.align.center}
            justify={theme.flex.justify.between}
            className={landingClasses.guaranteedBlocksItem}
          >
            <Box className={landingClasses.guaranteedBlocksItemTexts}>
              <Box className={landingClasses.guaranteedBlocksItemTextsTitle}>
                Trusted timestamps and data integrity
              </Box>

              <Text className={landingClasses.guaranteedBlocksItemTextsText}>
                The vBase app verifiably secures data, creating a timestamped
                record of your data’s existence.
              </Text>
            </Box>
            <GuaranteedDataBlock>
              <Box className={landingClasses.guaranteedFingerprint}>
                <Flex
                  justify={theme.flex.justify.center}
                  align={theme.flex.align.start}
                  className={landingClasses.guaranteedFingerprintTop}
                >
                  <Box
                    className={landingClasses.guaranteedFingerprintTopBrowse}
                  >
                    <Text component="span"> Browse for File</Text>
                    <Image
                      src={`${staticBasePath}public/images/svg/landing_browse_for_file.svg`}
                      alt="Browse For File"
                      className={
                        landingClasses.guaranteedFingerprintTopBrowseImg
                      }
                    />
                  </Box>
                  <Box
                    className={landingClasses.guaranteedFingerprintTopArrowWrap}
                  >
                    <Image
                      src={`${staticBasePath}public/images/svg/landing_arrow.svg`}
                      alt="Arrow"
                      className={landingClasses.guaranteedFingerprintTopArrow}
                    />
                  </Box>
                  <Image
                    src={`${staticBasePath}public/images/svg/landing_fingerprint.svg`}
                    alt="Fingerprint"
                    className={
                      landingClasses.guaranteedFingerprintTopFingerprint
                    }
                  />
                </Flex>
                <Flex
                  justify={theme.flex.justify.between}
                  align={theme.flex.align.start}
                  className={landingClasses.guaranteedFingerprintBottom}
                >
                  <Box
                    className={`${landingClasses.guaranteedFingerprintBottomItem} ${landingClasses.guaranteedFingerprintBottomLeft}`}
                  >
                    <Text component="span">
                      1. Calculate digital fingerprint
                    </Text>
                    <Text>
                      Load file into our browser app to privately calculate its
                      unique digital fingerprint (aka contentID).
                    </Text>
                  </Box>
                  <Box
                    className={`${landingClasses.guaranteedFingerprintBottomItem} ${landingClasses.guaranteedFingerprintBottomRight}`}
                  >
                    <Text component="span">
                      2. Publish fingerprint to blockchain
                    </Text>
                    <Text>
                      Verifiable fingerprint timestamp proves the file’s
                      existence and the stamp’s author.
                    </Text>
                  </Box>
                </Flex>
              </Box>
            </GuaranteedDataBlock>
          </Flex>

          <Flex
            align={theme.flex.align.center}
            justify={theme.flex.justify.between}
            className={landingClasses.guaranteedBlocksItem}
          >
            <GuaranteedDataBlock textRight="true">
              <Box className={landingClasses.guaranteedStamp}>
                <Image
                  src={`${staticBasePath}public/images/svg/landing_stamp_found.svg`}
                  alt="Green checkmark"
                  className={landingClasses.guaranteedStampResultImg}
                />
                <Title
                  className={landingClasses.guaranteedStampTitle}
                  order={2}
                >
                  Stamp Found!
                </Title>

                <Flex
                  className={landingClasses.guaranteedStampFlexBlock}
                  justify={theme.flex.justify.center}
                  align={theme.flex.align.center}
                  mb={theme.margin.mdSpacious}
                >
                  <Box>
                    <Box className={landingClasses.guaranteedStampUserNameKey}>
                      Username
                    </Box>
                    <Box
                      className={landingClasses.guaranteedStampUserNameValue}
                    >
                      MyCompany LLC
                    </Box>
                  </Box>
                  <Box>
                    <Box
                      className={
                        landingClasses.guaranteedStampVerifiedIdentityKey
                      }
                    >
                      Verified Identity
                    </Box>

                    <Box
                      component="span"
                      className={
                        landingClasses.guaranteedStampVerifiedIdentityValue
                      }
                    >
                      <Image
                        src={`${staticBasePath}public/images/svg/verified.svg`}
                        alt="verified"
                        className={
                          landingClasses.guaranteedStampVerifiedIdentityValueImg
                        }
                      />
                      Verified
                    </Box>
                  </Box>
                </Flex>
                <Box>
                  <Box
                    component="span"
                    className={landingClasses.guaranteedStampTimestampKey}
                  >
                    Timestamp:{" "}
                  </Box>
                  <Box
                    component="span"
                    className={landingClasses.guaranteedStampTimestampValue}
                  >
                    Dec 31, 2023 at 11:00:02 AM UTC &nbsp;&nbsp;{" "}
                  </Box>
                  <Box
                    component="span"
                    className={landingClasses.guaranteedStampTimestampAgo}
                  >
                    3 days ago
                  </Box>
                </Box>
              </Box>
            </GuaranteedDataBlock>

            <Box className={landingClasses.guaranteedBlocksItemTexts}>
              <Box className={landingClasses.guaranteedBlocksItemTextsTitle}>
                Verifiable anywhere, any time
              </Box>
              <Text className={landingClasses.guaranteedBlocksItemTextsText}>
                Build your credibility. Anyone with whom you share your file can{" "}
                <Text component="b">validate its timestamp and integrity</Text>{" "}
                by checking the blockchain fingerprint.
              </Text>
            </Box>
          </Flex>

          <Flex
            align={theme.flex.align.center}
            justify={theme.flex.justify.between}
            className={`${landingClasses.guaranteedBlocksItem} ${landingClasses.guaranteedBlocksItemSecure}`}
          >
            <Box className={landingClasses.guaranteedBlocksItemTexts}>
              <Box className={landingClasses.guaranteedBlocksItemTextsTitle}>
                Simple and Secure
              </Box>
              <Text className={landingClasses.guaranteedBlocksItemTextsText}>
                Your file never leaves your computer, while its timestamp
                remains independently verifiable forever.{" "}
              </Text>
            </Box>
            <GuaranteedDataBlock>
              <Flex justify={theme.flex.justify.center}>
                <Image
                  src={`${staticBasePath}public/images/svg/landing_secure.svg`}
                  alt="Simple and Secure"
                  className={landingClasses.guaranteedBlocksItemSecureImg}
                />
              </Flex>
            </GuaranteedDataBlock>
          </Flex>
        </Box>
      </Box>
    </Box>
  );
};

GuaranteedData.propTypes = {
  staticBasePath: PropTypes.string.isRequired,
};

export default GuaranteedData;
